/**
 * <burnout-hero>
 *
 * Auto-advancing hero slideshow.
 *
 * Same clock architecture as the announcement bar: the progress fill animation
 * *is* the timer, and advancing happens when it finishes. The indicator and the
 * actual timing therefore cannot drift apart, and pause/resume is one call that
 * stops both. When the progress indicator is switched off, an inert property is
 * animated on the host purely to keep time, so rotation never depends on a
 * visible element existing.
 *
 * Pauses on hover, on focus-within, and while the tab is hidden.
 */
class BurnoutHero extends HTMLElement {
  #slides = [];
  #fills = [];
  #index = 0;
  #interval = 6000;
  #animation = null;
  #paused = false;
  #pointerStartX = null;

  connectedCallback() {
    this.#slides = Array.from(this.querySelectorAll('.bo-hero__slide'));
    this.#fills = Array.from(this.querySelectorAll('.bo-hero__seg-fill'));
    this.#interval = Number(this.dataset.interval) || 6000;

    // Start from whichever slide the markup marked active, rather than
    // assuming the first, so JS state can't begin out of step with the paint.
    const rendered = this.#slides.findIndex((s) => s.classList.contains('is-active'));
    // First paint: the markup has no ratio yet.
    queueMicrotask(() => this.#syncRatio());
    this.#index = rendered === -1 ? 0 : rendered;

    if (this.#slides.length < 2) return;

    this.addEventListener('pointerenter', this.#pause);
    this.addEventListener('pointerleave', this.#resume);
    this.addEventListener('focusin', this.#pause);
    this.addEventListener('focusout', this.#resume);
    document.addEventListener('visibilitychange', this.#onVisibilityChange);

    this.querySelector('[data-bo-hero-prev]')?.addEventListener('click', () => this.#go(-1));
    this.querySelector('[data-bo-hero-next]')?.addEventListener('click', () => this.#go(1));

    this.querySelectorAll('[data-bo-hero-to]').forEach((dot) =>
      dot.addEventListener('click', () => this.#goTo(Number(dot.dataset.boHeroTo)))
    );

    this.addEventListener('pointerdown', this.#onPointerDown, { passive: true });
    this.addEventListener('pointerup', this.#onPointerUp, { passive: true });
    this.addEventListener('pointercancel', () => (this.#pointerStartX = null), { passive: true });

    this.#run();
  }

  disconnectedCallback() {
    this.#animation?.cancel();
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
  }

  #run = () => {
    this.#animation?.cancel();

    this.#fills.forEach((fill, i) => {
      fill.style.transform = i < this.#index ? 'scaleX(1)' : 'scaleX(0)';
    });

    const active = this.#fills[this.#index];
    const keyframes = active
      ? [{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }]
      : [{ opacity: 1 }, { opacity: 1 }];

    this.#animation = (active ?? this).animate(keyframes, {
      duration: this.#interval,
      easing: 'linear',
      fill: 'forwards',
    });

    this.#animation.onfinish = () => this.#go(1);
    if (this.#paused) this.#animation.pause();
  };

  #goTo = (target) => {
    if (target === this.#index || Number.isNaN(target)) return;
    this.#go(target > this.#index ? 1 : -1, target);
  };

  /**
   * Advance by `direction`, or jump straight to `target` when given.
   *
   * Every slide is recomputed from the resulting index rather than mutating
   * only the pair that changed, so state can't drift however the move started.
   */
  #go = (direction, target) => {
    const previous = this.#slides[this.#index];
    this.#index =
      typeof target === 'number'
        ? target
        : (this.#index + direction + this.#slides.length) % this.#slides.length;

    this.#slides.forEach((slide, i) => {
      const isActive = i === this.#index;
      slide.classList.toggle('is-active', isActive);
      slide.classList.toggle('is-leaving', slide === previous && !isActive);
      // Keep buttons inside hidden slides out of the tab order.
      slide.toggleAttribute('inert', !isActive);
    });

    this.#dots()?.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === this.#index);
      dot.setAttribute('aria-current', i === this.#index ? 'true' : 'false');
    });

    this.#syncRatio();
    this.#run();
  };

  /**
   * In "match image" height mode, give the stage the active image's own
   * proportions.
   *
   * The CSS used to size the stage from the first slide alone, which is fine
   * only while every banner shares a ratio. With a 375x468 and a 375x500 the
   * second was letterboxed inside the first one's box — black bars down the
   * sides, which is exactly what "match image" is supposed to prevent.
   *
   * naturalWidth/Height is read rather than any authored value because <picture>
   * swaps the source per breakpoint: this reports whichever file is actually in
   * use, so the mobile art sets the mobile height with nothing to configure.
   */
  #syncRatio = () => {
    if (!this.closest('.bo-hero--auto')) return;

    const img = this.#slides[this.#index]?.querySelector('.bo-hero__img');
    if (!img) return;

    const apply = () => {
      if (!img.naturalWidth || !img.naturalHeight) return;
      this.style.setProperty('--bo-hero-ratio', `${img.naturalWidth} / ${img.naturalHeight}`);
    };

    // A slide that has not decoded yet reports 0; wait for it rather than
    // writing a ratio of zero and collapsing the stage.
    if (img.complete) apply();
    else img.addEventListener('load', apply, { once: true });
  };

  #dots = () => {
    const dots = this.querySelectorAll('[data-bo-hero-to]');
    return dots.length ? Array.from(dots) : null;
  };

  #pause = () => {
    this.#paused = true;
    this.#animation?.pause();
  };

  #resume = () => {
    this.#paused = false;
    if (!document.hidden) this.#animation?.play();
  };

  #onVisibilityChange = () => {
    if (document.hidden) this.#pause();
    else this.#resume();
  };

  #onPointerDown = (event) => {
    if (event.pointerType === 'mouse') return;
    this.#pointerStartX = event.clientX;
  };

  #onPointerUp = (event) => {
    if (this.#pointerStartX === null) return;
    const delta = event.clientX - this.#pointerStartX;
    this.#pointerStartX = null;
    if (Math.abs(delta) < 45) return;
    this.#go(delta < 0 ? 1 : -1);
  };
}

if (!customElements.get('burnout-hero')) {
  customElements.define('burnout-hero', BurnoutHero);
}
